/**
 * SitesView - Operational dashboard of working sites
 *
 * Displays all sites as cards with aggregated KPIs:
 * - Driver count per site
 * - Vehicle count per site
 * - Tool count per site
 *
 * READ-ONLY for all users. Admin can navigate to Parametres for CRUD.
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Search, RefreshCw, Settings, XCircle, Building2, Warehouse, Car, Hammer, Map as MapIcon, GitBranch, Users, Navigation, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { workingSiteAPI } from '../../api';
import api from '../../api';
import { useAuth } from '../../v3/context/AuthContext.jsx';
import SiteDetailModal from './SiteDetailModal.jsx';
import SiteTreePanel from './SiteTreePanel.jsx'; // lot 3.1 : organigramme des sites
import MapWrapper, { L } from '../map/MapWrapper.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import SkeletonLoader from '../common/SkeletonLoader.jsx';

// Site type configuration
const SITE_TYPE_CONFIG = {
  siege: { color: 'purple', bgClass: 'bg-purple-100', textClass: 'text-purple-600', badgeClass: 'bg-purple-100 text-purple-800', Icon: Building2 },
  headquarters: { color: 'purple', bgClass: 'bg-purple-100', textClass: 'text-purple-600', badgeClass: 'bg-purple-100 text-purple-800', Icon: Building2 },
  agence: { color: 'blue', bgClass: 'bg-blue-100', textClass: 'text-blue-600', badgeClass: 'bg-blue-100 text-blue-800', Icon: Building2 },
  branch: { color: 'blue', bgClass: 'bg-blue-100', textClass: 'text-blue-600', badgeClass: 'bg-blue-100 text-blue-800', Icon: Building2 },
  depot: { color: 'amber', bgClass: 'bg-amber-100', textClass: 'text-amber-600', badgeClass: 'bg-amber-100 text-amber-800', Icon: Warehouse },
  parking: { color: 'gray', bgClass: 'bg-gray-100', textClass: 'text-gray-600', badgeClass: 'bg-gray-100 text-gray-800', Icon: Car },
  workshop: { color: 'orange', bgClass: 'bg-orange-100', textClass: 'text-orange-600', badgeClass: 'bg-orange-100 text-orange-800', Icon: Hammer },
  atelier: { color: 'orange', bgClass: 'bg-orange-100', textClass: 'text-orange-600', badgeClass: 'bg-orange-100 text-orange-800', Icon: Hammer },
  client_site: { color: 'blue', bgClass: 'bg-blue-100', textClass: 'text-blue-600', badgeClass: 'bg-blue-100 text-blue-800', Icon: Building2 },
  client: { color: 'blue', bgClass: 'bg-blue-100', textClass: 'text-blue-600', badgeClass: 'bg-blue-100 text-blue-800', Icon: Building2 },
  chantier: { color: 'orange', bgClass: 'bg-orange-100', textClass: 'text-orange-600', badgeClass: 'bg-orange-100 text-orange-800', Icon: Warehouse },
  temporary: { color: 'yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-600', badgeClass: 'bg-yellow-100 text-yellow-800', Icon: Warehouse },
  station: { color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-600', badgeClass: 'bg-green-100 text-green-800', Icon: Warehouse },
  autre: { color: 'gray', bgClass: 'bg-gray-100', textClass: 'text-gray-600', badgeClass: 'bg-gray-100 text-gray-800', Icon: MapPin }
};

function getTypeConfig(siteType) {
  return SITE_TYPE_CONFIG[siteType] || SITE_TYPE_CONFIG.autre;
}

// Libellé du type de site : résolu au rendu (jamais dans une constante de module)
function getTypeLabel(t, siteType) {
  const key = SITE_TYPE_CONFIG[siteType] ? siteType : 'autre';
  return t(`sites.types.${key}`);
}

// Map color config (hex colors for markers)
const MAP_COLORS = {
  siege: '#9333ea',
  headquarters: '#9333ea',
  agence: '#3b82f6',
  branch: '#3b82f6',
  depot: '#f59e0b',
  parking: '#6b7280',
  workshop: '#f97316',
  atelier: '#f97316',
  client_site: '#3b82f6',
  client: '#3b82f6',
  chantier: '#f97316',
  temporary: '#eab308',
  station: '#22c55e',
  autre: '#6b7280'
};

// Custom icon factory for map markers
const createSiteIcon = (color) => {
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    </div>`,
    className: 'custom-site-marker',
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

// Site popup for map
function SiteMapPopup({ site, onClick }) {
  const { t } = useTranslation();
  const config = getTypeConfig(site.site_type);
  const IconComponent = config.Icon;
  return (
    <div className="min-w-[200px]">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.bgClass}`}>
          <IconComponent className={`w-4 h-4 ${config.textClass}`} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{site.site_name}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${config.badgeClass}`}>
            {getTypeLabel(t, site.site_type)}
          </span>
        </div>
      </div>
      {site.address && <p className="text-sm text-gray-600 mb-2">{site.address}, {site.city}</p>}
      <div className="flex items-center gap-4 text-sm mb-3">
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4 text-green-500" />
          <span>{site.driver_count || 0}</span>
        </div>
        <div className="flex items-center gap-1">
          <Car className="w-4 h-4 text-blue-500" />
          <span>{site.vehicle_count || 0}</span>
        </div>
        <div className="flex items-center gap-1">
          <Hammer className="w-4 h-4 text-amber-500" />
          <span>{site.tool_count || 0}</span>
        </div>
      </div>
      <button
        onClick={() => onClick(site)}
        className="w-full px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
      >
        {t('sites.list.viewDetails')}
      </button>
    </div>
  );
}

function SiteCard({ site, onClick, onEdit, onDelete }) {
  const { t } = useTranslation();
  const config = getTypeConfig(site.site_type);
  const Icon = config.Icon;
  const isHQ = site.site_type === 'headquarters' || site.is_primary;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-purple-300 transition-all cursor-pointer relative group"
    >
      {/* Edit/Delete buttons (top-right, visible on hover) */}
      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit?.(site); }}
          className="p-1.5 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors shadow-sm"
          title={t('common.actions.edit')}
        >
          <Pencil className="w-3.5 h-3.5 text-gray-500 hover:text-blue-600" />
        </button>
        {!isHQ && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete?.(site); }}
            className="p-1.5 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors shadow-sm"
            title={t('sites.actions.deactivate')}
          >
            <Trash2 className="w-3.5 h-3.5 text-gray-500 hover:text-red-600" />
          </button>
        )}
      </div>

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${config.bgClass} rounded-lg`}>
              <Icon className={`w-5 h-5 ${config.textClass}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{site.site_name}</h3>
              <p className="text-sm text-gray-500">{site.site_code}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-2.5 py-1 ${config.badgeClass} text-xs font-medium rounded-full`}>
              {getTypeLabel(t, site.site_type)}
            </span>
            {isHQ && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-medium rounded-full">{t('sites.card.hqBadge')}</span>}
          </div>
        </div>

        {/* Address */}
        <div className="mb-4 text-sm text-gray-600">
          <p>{site.address || '-'}</p>
          <p>{site.postal_code} {site.city}</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3 py-4 border-t border-b border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{site.driver_count || 0}</p>
            <p className="text-xs text-gray-500 uppercase">{t('sites.stats.drivers')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{site.vehicle_count || 0}</p>
            <p className="text-xs text-gray-500 uppercase">{t('sites.stats.vehicles')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-600">{site.tool_count || 0}</p>
            <p className="text-xs text-gray-500 uppercase">{t('sites.stats.tools')}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm">
            <p className="text-gray-500">{t('sites.card.capacity')}</p>
            <p className="font-medium text-gray-900">{site.parking_capacity ? t('sites.card.capacityValue', { count: Number(site.parking_capacity) }) : '-'}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* GPS indicator */}
            {(() => {
              const hasGps = site.gps_latitude && site.gps_longitude &&
                !isNaN(parseFloat(site.gps_latitude)) && !isNaN(parseFloat(site.gps_longitude));
              return (
                <span title={hasGps ? t('sites.card.gpsSet') : t('sites.card.gpsMissing')}>
                  <Navigation className={`w-4 h-4 ${hasGps ? 'text-green-500' : 'text-gray-300'}`} />
                </span>
              );
            })()}
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${site.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              <span className={`text-sm ${site.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                {site.status === 'active' ? t('sites.status.active') : t('sites.status.inactive')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SitesView() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // All authenticated users with 'sites' in their profile modules can manage sites (read_only=0)
  // The ui_profile controls visibility; if user sees the Sites tab, they can manage
  const canManageSites = true;

  const { searchQuery } = useSearch();
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map' | 'org'
  const [selectedSite, setSelectedSite] = useState(null);
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null); // site object for editing, null for new

  // Fetch site stats
  const { data: statsData, isLoading, refetch } = useQuery({
    queryKey: ['site-stats'],
    queryFn: () => workingSiteAPI.getStats()
  });

  const sites = statsData?.data || [];
  const totals = statsData?.totals || { sites: 0, drivers: 0, vehicles: 0, tools: 0 };

  // Get unique site types for tabs
  const siteTypes = useMemo(() => {
    const types = {};
    sites.forEach(site => {
      const type = site.site_type || 'autre';
      types[type] = (types[type] || 0) + 1;
    });
    return types;
  }, [sites]);

  // Filter sites
  const filteredSites = useMemo(() => {
    return sites.filter(site => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!site.site_name?.toLowerCase().includes(q) &&
            !site.site_code?.toLowerCase().includes(q) &&
            !site.city?.toLowerCase().includes(q)) {
          return false;
        }
      }
      // Type filter
      if (typeFilter !== 'all' && site.site_type !== typeFilter) {
        return false;
      }
      return true;
    });
  }, [sites, searchQuery, typeFilter]);

  // Count sites with valid GPS coordinates (for tab badge)
  const sitesWithGPS = useMemo(() => {
    return filteredSites.filter(site => {
      const lat = parseFloat(site.gps_latitude);
      const lng = parseFloat(site.gps_longitude);
      const validLat = !isNaN(lat) && lat >= -90 && lat <= 90;
      const validLng = !isNaN(lng) && lng >= -180 && lng <= 180;
      return validLat && validLng;
    });
  }, [filteredSites]);

  // Memoize map markers to prevent re-renders from breaking zoom
  const mapMarkers = useMemo(() => {
    return sitesWithGPS.map(site => ({
      id: site.id,
      lat: site.gps_latitude,
      lng: site.gps_longitude,
      icon: createSiteIcon(MAP_COLORS[site.site_type] || MAP_COLORS.autre),
      popup: (
        <div className="min-w-[200px]">
          <div className="flex items-center gap-2 mb-2">
            <strong className="text-gray-900">{site.site_name}</strong>
          </div>
          <p className="text-sm text-gray-600 mb-2">{site.address}, {site.city}</p>
          <button
            onClick={() => setSelectedSite(site)}
            className="text-sm text-purple-600 hover:text-purple-800"
          >
            {t('sites.list.viewDetails')}
          </button>
        </div>
      )
    }));
  }, [sitesWithGPS, t]);

  const handleNavigateToAdmin = () => {
    // Navigate to Administration > Parametres > Sites tab
    window.location.hash = '#/settings?tab=sites';
  };

  return (
    <div className="p-6">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left: Icon + Title + Stats */}
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <MapPin className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('sites.list.title')}</h1>
              <p className="text-gray-500 text-sm">
                <span className="font-semibold text-purple-600">{t('sites.list.activeSites', { count: totals.sites })}</span>
                <span className="mx-2">|</span>
                <span className="text-green-600">{t('sites.list.driverCount', { count: totals.drivers })}</span>
                <span className="mx-2">|</span>
                <span className="text-blue-600">{t('sites.list.vehicleCount', { count: totals.vehicles })}</span>
              </p>
            </div>
          </div>

          {/* Right: Search + Refresh + Admin */}
          <div className="flex items-center gap-3">
            {/* Refresh */}
            <button
              onClick={() => refetch()}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title={t('common.actions.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

            {/* Add site button */}
            {canManageSites && (
              <button
                onClick={() => { setEditingSite(null); setShowSiteForm(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                {t('sites.actions.add')}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="flex items-center justify-between">
            {/* Type filter tabs */}
            <div className="flex gap-6">
              <button
                onClick={() => { setTypeFilter('all'); setViewMode('list'); }}
                className={`pb-3 border-b-2 font-medium text-sm ${
                  typeFilter === 'all' && viewMode === 'list'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('sites.list.tabAll', { count: sites.length })}
              </button>
              {Object.entries(siteTypes).map(([type, count]) => {
                return (
                  <button
                    key={type}
                    onClick={() => { setTypeFilter(type); setViewMode('list'); }}
                    className={`pb-3 border-b-2 font-medium text-sm ${
                      typeFilter === type && viewMode === 'list'
                        ? 'border-purple-600 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('sites.list.tabType', { label: getTypeLabel(t, type), count })}
                  </button>
                );
              })}
              {/* Carte tab */}
              <button
                onClick={() => setViewMode('map')}
                className={`pb-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                  viewMode === 'map'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <MapIcon className="w-4 h-4" />
                {t('sites.list.tabMap', { count: sitesWithGPS.length })}
              </button>
              {/* Organisation tab (lot 3.1) */}
              <button
                onClick={() => setViewMode('org')}
                className={`pb-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                  viewMode === 'org'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <GitBranch className="w-4 h-4" />
                {t('sites.list.tabOrg')}
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Content: Grid or Map */}
      {isLoading ? (
        <SkeletonLoader type="cards" cards={6} columns={3} />
      ) : viewMode === 'org' ? (
        <div className="bg-white rounded-xl shadow-sm p-6"><SiteTreePanel /></div>
      ) : viewMode === 'map' ? (
        /* Map View - Crash Isolated - uses memoized markers */
        <>
          <MapWrapper
            mapKey="sites-map-main"
            markers={mapMarkers}
            onRefresh={refetch}
            emptyMessage={t('sites.map.emptyTitle')}
            emptyDescription={t('sites.map.emptyDescription')}
          />
          {/* Map Legend - separate from MapWrapper to avoid stacking context issues */}
          {sitesWithGPS.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-3 mt-4">
              <p className="text-xs font-medium text-gray-700 mb-2">{t('sites.map.legend')}</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(siteTypes).map(([type]) => {
                  const color = MAP_COLORS[type] || MAP_COLORS.autre;
                  return (
                    <div key={type} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs text-gray-600">{getTypeLabel(t, type)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : filteredSites.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">{t('sites.list.empty')}</p>
        </div>
      ) : (
        /* List View - Sites Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredSites.map(site => (
            <SiteCard
              key={site.id}
              site={site}
              onClick={() => setSelectedSite(site)}
              onEdit={(s) => { setEditingSite(s); setShowSiteForm(true); }}
              onDelete={async (s) => {
                if (!window.confirm(t('sites.confirm.deactivate', { name: s.site_name }))) return;
                try {
                  await api.update('working_site', s.id, { status: 'inactive' });
                  refetch();
                } catch (err) {
                  alert(t('sites.errors.generic', { message: err.message }));
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Site Detail Modal */}
      {selectedSite && (
        <SiteDetailModal
          site={selectedSite}
          canManageSites={canManageSites}
          onClose={() => setSelectedSite(null)}
          onNavigateToAdmin={handleNavigateToAdmin}
          onSiteUpdated={() => { refetch(); setSelectedSite(null); }}
        />
      )}

      {/* Site Form Modal (Add/Edit) */}
      {showSiteForm && (
        <SiteFormModal
          site={editingSite}
          onClose={() => { setShowSiteForm(false); setEditingSite(null); }}
          onSuccess={() => { setShowSiteForm(false); setEditingSite(null); refetch(); }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
/* Site Form Modal — Add / Edit                                     */
/* ──────────────────────────────────────────────────────────────── */
// Valeurs de types proposées par le formulaire — le libellé est résolu au rendu
const SITE_TYPE_VALUES = ['headquarters', 'depot', 'workshop', 'parking', 'client', 'chantier', 'autre'];

function SiteFormModal({ site, onClose, onSuccess }) {
  const { t } = useTranslation();
  const isEdit = !!site;
  const isCurrentHQ = site?.site_type === 'headquarters' || !!site?.is_primary;
  const [form, setForm] = useState({
    site_name: site?.site_name || '',
    site_code: site?.site_code || '',
    site_type: site?.site_type || 'depot',
    address: site?.address || '',
    postal_code: site?.postal_code || '',
    city: site?.city || '',
    country: site?.country || 'France',
    contact_phone: site?.contact_phone || '',
    contact_email: site?.contact_email || '',
    parking_capacity: site?.parking_capacity || '',
    status: site?.status || 'active',
    is_primary: site?.is_primary || 0,
  });
  const [promoteToHQ, setPromoteToHQ] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!form.site_name.trim()) { setError(t('sites.errors.nameRequired')); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };

      // If promoting to HQ: set type + is_primary, demote current HQ
      if (promoteToHQ && !isCurrentHQ) {
        payload.site_type = 'headquarters';
        payload.is_primary = 1;
        // Demote current HQ(s) — fetch all sites, find current HQ, update it
        try {
          const allRes = await workingSiteAPI.getAll();
          const currentHQs = (allRes?.data || []).filter(s => (s.site_type === 'headquarters' || s.is_primary) && s.id !== site?.id);
          for (const hq of currentHQs) {
            await api.update('working_site', hq.id, { site_type: 'depot', is_primary: 0 });
          }
        } catch { /* best effort */ }
      }

      if (isEdit) {
        await api.update('working_site', site.id, payload);
      } else {
        const code = payload.site_code || `SITE-${String(Date.now()).slice(-6)}`;
        await api.create('working_site', { ...payload, site_code: code });
      }
      onSuccess();
    } catch (err) {
      setError(err.message || t('sites.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!isEdit || isCurrentHQ) return;
    if (!window.confirm(t('sites.confirm.deactivateDetailed', { name: site.site_name }))) return;
    setSaving(true);
    try {
      await api.update('working_site', site.id, { status: 'inactive' });
      onSuccess();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-[600px] max-h-[85vh] flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-white" />
            <h2 className="text-lg font-semibold text-white">{isEdit ? t('sites.form.editTitle') : t('sites.form.createTitle')}</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.name')}</label>
              <input value={form.site_name} onChange={e => set('site_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                placeholder={t('sites.form.namePlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.code')}</label>
              <input value={form.site_code} onChange={e => set('site_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder={t('sites.form.codePlaceholder')} disabled={isEdit} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.type')}</label>
            <div className="grid grid-cols-4 gap-2">
              {SITE_TYPE_VALUES.map(value => (
                <button key={value} onClick={() => set('site_type', value)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border-2 transition-colors ${
                    form.site_type === value ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}>{t(`sites.form.types.${value}`)}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.address')}</label>
            <input value={form.address} onChange={e => set('address', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder={t('sites.form.addressPlaceholder')} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.postalCode')}</label>
              <input value={form.postal_code} onChange={e => set('postal_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.city')}</label>
              <input value={form.city} onChange={e => set('city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.country')}</label>
              <input value={form.country} onChange={e => set('country', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.phone')}</label>
              <input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('sites.form.parkingCapacity')}</label>
              <input type="number" value={form.parking_capacity} onChange={e => set('parking_capacity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder={t('sites.form.parkingCapacityPlaceholder')} />
            </div>
          </div>

          {/* HQ promotion */}
          {isEdit && !isCurrentHQ && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={promoteToHQ} onChange={e => setPromoteToHQ(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500" />
                <div>
                  <span className="text-sm font-medium text-amber-900">{t('sites.form.promoteHq')}</span>
                  <p className="text-xs text-amber-700 mt-0.5">{t('sites.form.promoteHqHint')}</p>
                </div>
              </label>
            </div>
          )}
          {isCurrentHQ && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700 flex items-center gap-2">
              <Building2 className="w-4 h-4 flex-shrink-0" />
              {t('sites.form.hqNotice')}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div>
            {isEdit && !isCurrentHQ && (
              <button onClick={handleDeactivate} disabled={saving}
                className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1">
                <Trash2 className="w-4 h-4" /> {t('sites.actions.deactivate')}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">{t('common.actions.cancel')}</button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 flex items-center gap-1">
              <Check className="w-4 h-4" /> {saving ? t('common.states.saving') : isEdit ? t('common.actions.save') : t('sites.form.submitCreate')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SitesView;
